// v2.16.0a — DivertissementCard refondue pour matcher exactement le pattern
// `FeaturedCards.jsx` du mode Quotidien : 240px de large, image 110px de haut,
// border-radius 14, typography 13.5/11.5, scroll horizontal snap, hover glow.
//
// v2.16.1 — Matrice de boutons métier par catégorie (2e ligne sous les boutons
// génériques 🚀 GPS / 📋 / ➕). Real actions : 🛵 Yango (deeplink natif+fallback),
// 📞 Appeler (tel:). Autres actions : toast "Bientôt disponible".

import { useState } from "react";
import { toast } from "sonner";
import PlatformOfferModal from "./PlatformOfferModal";

const TIER_BADGE_COLOR = {
  partner: "linear-gradient(135deg, #FCD34D, #F59E0B)",
  premium: "linear-gradient(135deg, #C4B5FD, #A855F7)",
  basic:   "linear-gradient(135deg, #93C5FD, #3B82F6)",
};

// v3.23.0 — Bloc réservation Beauté (jour + créneau → POST /beaute/:id/book).
const BOOK_SLOTS = ["09h00", "11h00", "14h00", "16h00", "18h00"];
export function BeautyBookingBlock({ item, accent }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState("Aujourd'hui");
  const [slot, setSlot] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | done
  const [booking, setBooking] = useState(null);
  const confirm = async (e) => {
    e.stopPropagation();
    const token = localStorage.getItem("civ_user_token");
    if (!token) { toast.error("Connecte-toi pour réserver ton créneau"); return; }
    if (!slot) { toast.error("Choisis un créneau"); return; }
    setState("loading");
    try {
      const API = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${API}/api/v3/beaute/${item.id}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ day, slot }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || "réservation impossible");
      setBooking(d.booking);
      setState("done");
      toast.success(`💅 Réservé chez ${item.nom} — ${day} à ${slot}`);
    } catch (err) {
      setState("idle");
      toast.error(`Réservation impossible : ${err?.message || err}`);
    }
  };
  if (state === "done" && booking) {
    return (
      <div data-testid={`beaute-booked-${item.id}`} style={{ marginTop: 6, padding: "7px 10px", borderRadius: 8, background: "rgba(0,255,136,0.10)", border: "1px solid rgba(0,255,136,0.4)", fontSize: 11.5, fontWeight: 700, color: "#6EE7B7" }}>
        ✅ Réservé {booking.day.toLowerCase()} à {booking.slot} · code {String(booking.booking_id).slice(-6).toUpperCase()}
      </div>
    );
  }
  if (!open) {
    return (
      <div style={{ marginTop: 6 }}>
        <button
          type="button"
          data-testid={`beaute-book-btn-${item.id}`}
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          style={{ width: "100%", background: accent, border: "none", color: "#081210", padding: "6px 10px", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer" }}
        >📅 Réserver un créneau</button>
      </div>
    );
  }
  const chip = (active) => ({
    padding: "4px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, cursor: "pointer",
    background: active ? accent : "rgba(255,255,255,0.07)",
    color: active ? "#081210" : "#CBD5E1",
    border: `1px solid ${active ? accent : "rgba(255,255,255,0.18)"}`,
  });
  return (
    <div data-testid={`beaute-book-panel-${item.id}`} onClick={(e) => e.stopPropagation()} style={{ marginTop: 6, padding: 8, borderRadius: 10, background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.12)" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        {["Aujourd'hui", "Demain"].map((dd) => (
          <button key={dd} type="button" style={chip(day === dd)} onClick={() => setDay(dd)}>{dd}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 7 }}>
        {BOOK_SLOTS.map((s) => (
          <button key={s} type="button" data-testid={`beaute-slot-${item.id}-${s}`} style={chip(slot === s)} onClick={() => setSlot(s)}>{s}</button>
        ))}
      </div>
      <button
        type="button"
        data-testid={`beaute-book-confirm-${item.id}`}
        onClick={confirm}
        disabled={state === "loading"}
        style={{ width: "100%", background: accent, border: "none", color: "#081210", padding: "6px 10px", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer", opacity: state === "loading" ? 0.6 : 1 }}
      >{state === "loading" ? "⏳ Réservation…" : `✅ Confirmer ${slot ? `${day.toLowerCase()} · ${slot}` : ""}`}</button>
    </div>
  );
}

function formatFCFA(n) {
  if (n == null || n === 0) return null;
  return new Intl.NumberFormat("fr-FR").format(n) + " FCFA";
}

// ─── v2.16.1 — Matrice boutons métier par catégorie ────────────────────────
const CATEGORY_ACTIONS_MAP = {
  cinema:   [{ slug: "bande-annonce", emoji: "▶️", label: "Bande annonce" }, { slug: "reserver-place", emoji: "🎫", label: "Réserver place" }],
  concerts: [{ slug: "billet",        emoji: "🎫", label: "Billet" },        { slug: "date",           emoji: "📅", label: "Date" }],
  maquis:   [{ slug: "appeler",       emoji: "📞", label: "Appeler", real: "call" }, { slug: "livraison-yango", emoji: "🛵", label: "Livraison Yango", real: "yango" }],
  bars:     [{ slug: "appeler",       emoji: "📞", label: "Appeler", real: "call" }, { slug: "livraison-yango", emoji: "🛵", label: "Livraison Yango", real: "yango" }],
  boites:   [{ slug: "table-vip",     emoji: "🎟️", label: "Table VIP" },     { slug: "reserver",       emoji: "📅", label: "Réserver" }],
  karaoke:  [{ slug: "reserver-salle", emoji: "🎤", label: "Réserver salle" }, { slug: "appeler",       emoji: "📞", label: "Appeler", real: "call" }],
  jeux:     [{ slug: "session",        emoji: "🎮", label: "Session" },        { slug: "tarifs",         emoji: "💸", label: "Tarifs" }],
  sport:    [{ slug: "billet-match",   emoji: "🎫", label: "Billet match" },   { slug: "date",           emoji: "📅", label: "Date" }],
  culture:  [{ slug: "billet",         emoji: "🎫", label: "Billet" },         { slug: "programme",      emoji: "ℹ️", label: "Programme" }],
  soirees:  [{ slug: "contacter-organisateur", emoji: "📞", label: "Contacter organisateur", real: "call" }],
  piscine:  [{ slug: "entree",         emoji: "🎫", label: "Entrée" },         { slug: "horaires",       emoji: "📅", label: "Horaires" }],
  foraine:  [{ slug: "pass-journee",   emoji: "🎫", label: "Pass journée" },   { slug: "horaires",       emoji: "📅", label: "Horaires" }],
};

function getCategoryActions(category) {
  return CATEGORY_ACTIONS_MAP[category] || [];
}

// Yango deeplink natif + fallback web (pattern v2.6.0 MonComptePage).
function openYangoForCard(item) {
  const labelEnc = encodeURIComponent(item.nom || "");
  const nativeUrl = `yango://route?dest_lat=${item.lat}&dest_lng=${item.lng}&dest_label=${labelEnc}`;
  const webUrl = `https://yango.com/?utm_source=civ-ai&dest_lat=${item.lat}&dest_lng=${item.lng}`;
  const t0 = Date.now();
  window.location.href = nativeUrl;
  setTimeout(() => {
    if (Date.now() - t0 < 1800 && document.visibilityState === "visible") {
      toast.message("Yango app non détectée — ouverture de yango.com");
      window.open(webUrl, "_blank");
    }
  }, 1500);
}

export default function DivertissementCard({ item, onAskMore, fluid = false }) {
  const [offerOpen, setOfferOpen] = useState(false);
  const [hovering, setHovering] = useState(false);
  if (!item) return null;
  const tier = item.sponsored_tier || "free";
  const hasOffer = tier !== "free" && item.platform_offer;
  const accent = item.accent || "#A855F7";
  const priceLabel = formatFCFA(item.tarif_fcfa);
  const categoryActions = getCategoryActions(item.category);

  const goNavigate = (e) => {
    e?.stopPropagation();
    const url = `/navigate?dest=${item.lat},${item.lng}&code=${encodeURIComponent(item.adresse_civ)}&label=${encodeURIComponent(item.nom)}`;
    window.location.href = url;
  };

  const copyCode = async (e) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(item.adresse_civ);
      toast.success(`📋 ${item.adresse_civ}`);
    } catch (_) { toast.error("Copie impossible"); }
  };

  const handleCategoryAction = (action) => {
    if (action.real === "yango") { openYangoForCard(item); return; }
    if (action.real === "call") {
      if (!item.telephone) { toast.error("Numéro indisponible"); return; }
      window.location.href = `tel:${item.telephone.replace(/\s+/g, "")}`;
      return;
    }
    toast.message("Bientôt disponible — Cette fonctionnalité arrive prochainement");
  };

  return (
    <article
      data-testid={`div-card-${item.id}`}
      className="civ-mode-card"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        position: "relative", borderRadius: 14, overflow: "hidden",
        background: "#0f172a",
        border: `1px solid ${hovering ? accent : "rgba(255,255,255,0.08)"}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.18), 0 6px 14px rgba(0,0,0,0.22)",
        display: "flex", flexDirection: "column",
        transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
        transform: hovering ? "translateY(-2px)" : "translateY(0)",
        ...(fluid ? { width: "100%" } : { flex: "0 0 240px", width: 240, scrollSnapAlign: "start" }),
      }}
    >
      <div style={{ position: "relative", height: 110, background: "#1e293b" }}>
        {item.image_url ? (
          <img src={item.image_url} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : (
          <div style={{
            width: "100%", height: "100%",
            background: `linear-gradient(135deg, ${accent}AA, ${accent}33)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 800, fontSize: 28,
          }}>{item.category_emoji}</div>
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.75) 100%)" }} />
        <span style={{
          position: "absolute", top: 8, left: 8,
          background: accent, color: "#fff",
          padding: "2px 8px", borderRadius: 999,
          fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em",
          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }}>
          {item.category_emoji} {(item.category_label || "").toUpperCase().slice(0, 14)}
        </span>
        {/* v3.22.0 — Badge "près de toi" (commune de l'utilisateur). */}
        {item.near_you && (
          <span data-testid={`div-near-badge-${item.id}`} style={{
            position: "absolute", bottom: 8, left: 8,
            background: "#00FF88", color: "#052012",
            padding: "2px 8px", borderRadius: 999,
            fontSize: 9.5, fontWeight: 800, letterSpacing: "0.04em",
            boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
          }}>📍 Près de toi</span>
        )}
        {hasOffer && (
          <button
            type="button"
            data-testid={`div-offer-badge-${item.id}`}
            onClick={(e) => { e.stopPropagation(); setOfferOpen(true); }}
            style={{
              position: "absolute", top: 8, right: 8,
              background: TIER_BADGE_COLOR[tier] || accent, color: "#0F172A",
              padding: "2px 8px", borderRadius: 999, border: "none",
              fontSize: 9.5, fontWeight: 800, letterSpacing: "0.04em",
              cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
            }}
            title="Voir l'offre plateforme"
          >🎁 Offre AYA</button>
        )}
      </div>

      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#fff", lineHeight: 1.25 }}>{item.nom}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
          📍 {item.commune_name} · <code style={{ color: "rgba(255,255,255,0.75)", fontSize: 10.5 }}>{item.adresse_civ}</code>
          {Number.isFinite(item.distance_km) && <span style={{ color: "#00FF88", fontWeight: 800 }}> · 📏 {item.distance_km} km</span>}
        </div>
        {item.horaires && (
          <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.65)", lineHeight: 1.35 }}>🕐 {item.horaires}</div>
        )}
        {priceLabel && (
          <div style={{ fontSize: 11.5, color: accent, fontWeight: 700, marginTop: 2 }}>💰 dès {priceLabel}</div>
        )}
        {/* Ligne 1 — Boutons génériques */}
        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button type="button" data-testid={`div-btn-navigate-${item.id}`} onClick={goNavigate} style={genericPrimary(accent)}>🚀 GPS</button>
          <button type="button" data-testid={`div-btn-copy-${item.id}`} onClick={copyCode} style={genericSecondary}>📋</button>
        </div>
        {/* v2.16.1 — Ligne 2 — Boutons métier par catégorie */}
        {categoryActions.length > 0 && (
          <div
            data-testid={`div-card-actions-row-${item.id}`}
            style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}
          >
            {categoryActions.map((act) => (
              <button
                key={act.slug}
                type="button"
                data-testid={`div-card-action-${item.category}-${act.slug}`}
                onClick={(e) => { e.stopPropagation(); handleCategoryAction(act); }}
                style={businessAction(act.real, accent)}
                title={act.label}
              >
                <span>{act.emoji}</span>
                <span style={{ marginLeft: 4 }}>{act.label}</span>
              </button>
            ))}
          </div>
        )}
        {/* v3.24.0 — Mini-site : "Voir le salon / le hammam / le resto…". */}
        {(String(item.id || "").startsWith("beaute-") || String(item.id || "").startsWith("food-")) && (
          <button
            type="button"
            data-testid={`place-site-btn-${item.id}`}
            onClick={(e) => { e.stopPropagation(); window.location.href = `/place/${item.id}`; }}
            style={{ width: "100%", marginTop: 6, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.2)", color: "#F4F1E8", padding: "6px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          >{(() => {
            const c = item.category;
            if (c === "hammam") return "🧖🏾‍♀️ Voir le hammam";
            if (c === "massage_spa") return "💆🏾‍♀️ Voir le spa";
            if (c === "fitness") return "🏋🏾‍♀️ Voir la salle";
            if (String(item.id).startsWith("beaute-")) return "💈 Voir le salon";
            if (c === "epicerie") return "🛒 Voir la boutique";
            return "🍽️ Voir le resto";
          })()}</button>
        )}
        {/* v3.23.0 — Réservation Beauté : créneau direct depuis la carte. */}
        {String(item.id || "").startsWith("beaute-") && (
          <BeautyBookingBlock item={item} accent={accent} />
        )}
        {onAskMore && (
          <div style={{ marginTop: 6 }}>
            <button
              type="button"
              data-testid={`div-btn-more-${item.id}`}
              onClick={(e) => { e.stopPropagation(); onAskMore(item); }}
              style={{
                width: "100%",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: "#CBD5E1",
                padding: "5px 10px", borderRadius: 8,
                fontSize: 11.5, fontWeight: 600, cursor: "pointer",
              }}
            >➕ En savoir plus</button>
          </div>
        )}
      </div>

      <PlatformOfferModal open={offerOpen} onClose={() => setOfferOpen(false)} item={item} />
    </article>
  );
}

// ─── Styles boutons compacts ────────────────────────────────────────────────
const genericPrimary = (accent) => ({
  background: accent, color: "#fff", border: "none",
  padding: "5px 12px", borderRadius: 8,
  fontSize: 11.5, fontWeight: 600, cursor: "pointer",
});
const genericSecondary = {
  background: "rgba(255,255,255,0.06)", color: "#CBD5E1",
  border: "1px solid rgba(255,255,255,0.18)",
  padding: "5px 10px", borderRadius: 8,
  fontSize: 11.5, fontWeight: 600, cursor: "pointer",
};
function businessAction(real, accent) {
  if (real === "yango") {
    return { background: "#FF6B00", color: "#fff", border: "none",
      padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer",
      display: "inline-flex", alignItems: "center" };
  }
  if (real === "call") {
    return { background: "#16A34A", color: "#fff", border: "none",
      padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer",
      display: "inline-flex", alignItems: "center" };
  }
  return { background: `${accent}22`, color: "#fff",
    border: `1px solid ${accent}66`,
    padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer",
    display: "inline-flex", alignItems: "center" };
}
